import { useProviderFromContext } from "@@/providers/state/contexts/use-provider-from-context";
import { useProvider } from "@@/providers/state/queries/provider/use-provider";
import { browserLogger } from "@@/settings";
import type { LanguageCode, Provider, ProviderId } from "@towni/common";
import { isProviderId, languageCodeMap } from "@towni/common";
import { useMemo } from "react";

type Result = readonly [
    {
        languages: LanguageCode[] | readonly LanguageCode[];
        /**
         * If the languages in the languages array was set deliberately and
         * chosen for the provider, defined is true, else it's false */
        defined: boolean;
    },
    pending: boolean,
];

const pendingResult: Result = [
    {
        languages: [],
        defined: false,
    },
    true,
];
const noProviderResult: Result = [
    {
        languages: [],
        defined: false,
    },
    false,
];

/**
 * Custom hook to retrieve the languages available for a given provider.
 * If it returns an empty array, it means that the provider does not have any languages set, and no languages has ever been set.
 *
 * @param {ProviderId | Provider} [provider] - The provider or provider ID to retrieve languages for. If not provided, the provider will be retrieved from context.
 * @returns {Result} - A tuple containing the list of languages and a boolean indicating if the provider data is still being fetched.
 *
 * @throws {Error} - Throws an error if no provider is found in given arguments or current context.
 */
const useProviderLanguages = (provider?: ProviderId | Provider): Result => {
    const providerFromContext = useProviderFromContext({
        disableThrowWhenMissingContext: true,
    });
    const [providerFromId, providerQuery] = useProvider(
        isProviderId(provider) ? provider : undefined,
    );
    const isPending = providerQuery.isPending && isProviderId(provider);
    const _provider = isProviderId(provider)
        ? providerFromId
        : (provider ?? providerFromContext);

    const result = useMemo(() => {
        if (!_provider && isPending) return pendingResult;

        if (!_provider) {
            browserLogger.warn("No provider found in useProviderLanguages");
            return noProviderResult;
        }

        return [
            {
                languages: _provider.options.languages ?? [languageCodeMap.sv],
                defined: !!_provider.options.languages,
            },
            isPending,
        ] as const;
    }, [_provider, isPending]);

    return result;
};

export { useProviderLanguages };
